import { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazyWithRetry, OfflineBanner } from "@dwelldrop/shared";
import ErrorBoundary from "./components/ErrorBoundary";
import { AdminAuthProvider } from "./contexts/AdminAuthContext";
import AdminProtectedRoute from "./components/AdminProtectedRoute";
import AdminLayout from "./components/AdminLayout";
import RouteAnnouncer from "./components/RouteAnnouncer";
import NavigationProgress from "./components/NavigationProgress";
import LoadingSpinner from "./components/LoadingSpinner";
import NewVersionBanner from "./components/NewVersionBanner";
import NewVersionModal from "./components/NewVersionModal";
import "./index.css";

// Route-level code splitting — auto-reloads on stale chunks after deploy
const LoginPage = lazyWithRetry(() => import("./pages/LoginPage"));
const AdminVerify2FAPage = lazyWithRetry(
  () => import("./pages/AdminVerify2FAPage")
);
const AdminSetup2FAPage = lazyWithRetry(
  () => import("./pages/AdminSetup2FAPage")
);
const AccessDeniedPage = lazyWithRetry(
  () => import("./pages/AccessDeniedPage")
);
const DashboardPage = lazyWithRetry(() => import("./pages/DashboardPage"));
const ConsumersPage = lazyWithRetry(() => import("./pages/ConsumersPage"));
const VenuesPage = lazyWithRetry(() => import("./pages/VenuesPage"));
const DealsPage = lazyWithRetry(() => import("./pages/DealsPage"));
const CreateDealPage = lazyWithRetry(() => import("./pages/CreateDealPage"));
const EditDealPage = lazyWithRetry(() => import("./pages/EditDealPage"));
const MonitoringPage = lazyWithRetry(() => import("./pages/MonitoringPage"));
const FraudDashboardPage = lazyWithRetry(
  () => import("./pages/FraudDashboardPage")
);
const PropertyOperatorPage = lazyWithRetry(
  () => import("./pages/PropertyOperatorPage")
);
const ConsumerDetailPage = lazyWithRetry(
  () => import("./pages/ConsumerDetailPage")
);
const VenueDetailPage = lazyWithRetry(() => import("./pages/VenueDetailPage"));
const DataRequestsPage = lazyWithRetry(
  () => import("./pages/DataRequestsPage")
);
const AuditLogsPage = lazyWithRetry(() => import("./pages/AuditLogsPage"));
const ShortlinksPage = lazyWithRetry(() => import("./pages/ShortlinksPage"));
const ShortlinksTrackingPage = lazyWithRetry(
  () => import("./pages/ShortlinksTrackingPage")
);
const ShortlinksReportPage = lazyWithRetry(
  () => import("./pages/ShortlinksReportPage")
);
const ConsentManagementPage = lazyWithRetry(
  () => import("./pages/ConsentManagementPage")
);
const RetentionRulesPage = lazyWithRetry(
  () => import("./pages/RetentionRulesPage")
);
const ThirdPartyCompliancePage = lazyWithRetry(
  () => import("./pages/ThirdPartyCompliancePage")
);
const PolicyVersioningPage = lazyWithRetry(
  () => import("./pages/PolicyVersioningPage")
);
const DPAManagementPage = lazyWithRetry(
  () => import("./pages/DPAManagementPage")
);
const VenueOwnersPage = lazyWithRetry(() => import("./pages/VenueOwnersPage"));
const AdminUsersPage = lazyWithRetry(() => import("./pages/AdminUsersPage"));
const PropertyOperatorsPage = lazyWithRetry(
  () => import("./pages/PropertyOperatorsPage")
);
const PropertiesPage = lazyWithRetry(() => import("./pages/PropertiesPage"));
const PropertyDetailPage = lazyWithRetry(
  () => import("./pages/PropertyDetailPage")
);
const DisputesPage = lazyWithRetry(() => import("./pages/DisputesPage"));
const CommissionsPage = lazyWithRetry(() => import("./pages/CommissionsPage"));
const SetPasswordPage = lazyWithRetry(() => import("./pages/SetPasswordPage"));
const ForgotPasswordPage = lazyWithRetry(
  () => import("./pages/ForgotPasswordPage")
);
const BreachManagementPage = lazyWithRetry(
  () => import("./pages/BreachManagementPage")
);
const DPOSettingsPage = lazyWithRetry(() => import("./pages/DPOSettingsPage"));
const SettingsPage = lazyWithRetry(() => import("./pages/SettingsPage"));
const GlobalConfigPage = lazyWithRetry(
  () => import("./pages/GlobalConfigPage")
);
const SLADashboardPage = lazyWithRetry(
  () => import("./pages/SLADashboardPage")
);
const ReportPage = lazyWithRetry(() => import("./pages/ReportPage"));
const ErrorRatePage = lazyWithRetry(() => import("./pages/ErrorRatePage"));
const AlertsPage = lazyWithRetry(() => import("./pages/AlertsPage"));
const AlertDetailPage = lazyWithRetry(() => import("./pages/AlertDetailPage"));
const InvoicesPage = lazyWithRetry(() => import("./pages/InvoicesPage"));
const InvoiceDetailPage = lazyWithRetry(
  () => import("./pages/InvoiceDetailPage")
);
const CategoriesPage = lazyWithRetry(() => import("./pages/CategoriesPage"));
const LegalEntitiesPage = lazyWithRetry(
  () => import("./pages/LegalEntitiesPage")
);
const PopularSearchesPage = lazyWithRetry(
  () => import("./pages/PopularSearchesPage")
);
const QRCodesPage = lazyWithRetry(() => import("./pages/QRCodesPage"));
const PointsLedgerPage = lazyWithRetry(
  () => import("./pages/PointsLedgerPage")
);
const PointsAdjustmentsPage = lazyWithRetry(
  () => import("./pages/PointsAdjustmentsPage")
);
const PointsReversalsPage = lazyWithRetry(
  () => import("./pages/PointsReversalsPage")
);

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <NavigationProgress />
        <RouteAnnouncer />
        <NewVersionBanner />
        <NewVersionModal />
        <OfflineBanner
          title="You're Offline"
          message="Admin operations are unavailable while offline. Reconnect to continue managing the platform."
          bannerMessage="You're offline. Admin actions are paused until the connection is restored."
          pillText="Offline — admin paused"
          pillOffsetClass="top-16"
        />
        <AdminAuthProvider>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/verify-2fa" element={<AdminVerify2FAPage />} />
              <Route path="/access-denied" element={<AccessDeniedPage />} />
              <Route path="/set-password" element={<SetPasswordPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />

              {/* Protected admin routes */}
              <Route
                element={
                  <AdminProtectedRoute>
                    <AdminLayout />
                  </AdminProtectedRoute>
                }
              >
                <Route index element={<DashboardPage />} />
                <Route path="consumers" element={<ConsumersPage />} />
                <Route path="consumers/:id" element={<ConsumerDetailPage />} />
                <Route path="venues" element={<VenuesPage />} />
                <Route path="venues/:id" element={<VenueDetailPage />} />
                <Route path="venue-owners" element={<VenueOwnersPage />} />
                <Route path="admin-users" element={<AdminUsersPage />} />
                <Route
                  path="property-operators"
                  element={<PropertyOperatorsPage />}
                />
                <Route path="deals" element={<DealsPage />} />
                <Route path="deals/new" element={<CreateDealPage />} />
                <Route path="deals/:id/edit" element={<EditDealPage />} />
                <Route path="categories" element={<CategoriesPage />} />
                <Route path="legal-entities" element={<LegalEntitiesPage />} />
                <Route path="properties" element={<PropertiesPage />} />
                <Route path="properties/:id" element={<PropertyDetailPage />} />
                <Route path="monitoring" element={<MonitoringPage />} />
                <Route path="sla" element={<SLADashboardPage />} />
                <Route path="error-rates" element={<ErrorRatePage />} />
                <Route path="fraud" element={<FraudDashboardPage />} />
                <Route path="alerts" element={<AlertsPage />} />
                <Route path="alerts/:id" element={<AlertDetailPage />} />
                <Route
                  path="property-operator"
                  element={<PropertyOperatorPage />}
                />
                <Route path="data-requests" element={<DataRequestsPage />} />
                <Route path="audit-logs" element={<AuditLogsPage />} />
                <Route path="shortlinks" element={<ShortlinksPage />} />
                <Route
                  path="shortlinks/tracking"
                  element={<ShortlinksTrackingPage />}
                />
                <Route
                  path="shortlinks/report"
                  element={<ShortlinksReportPage />}
                />
                <Route
                  path="consent-management"
                  element={<ConsentManagementPage />}
                />
                <Route
                  path="retention-rules"
                  element={<RetentionRulesPage />}
                />
                <Route
                  path="third-party"
                  element={<ThirdPartyCompliancePage />}
                />
                <Route
                  path="third-party-compliance"
                  element={<Navigate to="/third-party" replace />}
                />
                <Route
                  path="policy-versions"
                  element={<PolicyVersioningPage />}
                />
                <Route
                  path="policy-versioning"
                  element={<Navigate to="/policy-versions" replace />}
                />
                <Route path="dpa-management" element={<DPAManagementPage />} />
                <Route path="disputes" element={<DisputesPage />} />
                <Route path="transactions" element={<CommissionsPage />} />
                <Route path="points-ledger" element={<PointsLedgerPage />} />
                <Route
                  path="points-adjustments"
                  element={<PointsAdjustmentsPage />}
                />
                <Route
                  path="points-reversals"
                  element={<PointsReversalsPage />}
                />
                <Route path="invoices" element={<InvoicesPage />} />
                <Route path="invoices/:id" element={<InvoiceDetailPage />} />
                <Route
                  path="popular-searches"
                  element={<PopularSearchesPage />}
                />
                <Route path="qr-codes" element={<QRCodesPage />} />
                <Route path="reports" element={<ReportPage />} />
                <Route
                  path="breach-management"
                  element={<BreachManagementPage />}
                />
                <Route path="dpo-settings" element={<DPOSettingsPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="settings/2fa" element={<AdminSetup2FAPage />} />
                <Route path="setup-2fa" element={<Navigate to="/settings/2fa" replace />} />
                <Route path="global-config" element={<GlobalConfigPage />} />
              </Route>

              {/* PDPA alias routes */}
              <Route
                element={
                  <AdminProtectedRoute>
                    <AdminLayout />
                  </AdminProtectedRoute>
                }
              >
                <Route
                  path="pdpa/data-requests"
                  element={<DataRequestsPage />}
                />
                <Route
                  path="pdpa/consents"
                  element={<ConsentManagementPage />}
                />
                <Route path="pdpa/retention" element={<RetentionRulesPage />} />
                <Route
                  path="pdpa/third-party"
                  element={<ThirdPartyCompliancePage />}
                />
                <Route
                  path="pdpa/policies"
                  element={<PolicyVersioningPage />}
                />
                <Route path="pdpa/dpa" element={<DPAManagementPage />} />
                <Route
                  path="pdpa/breaches"
                  element={<BreachManagementPage />}
                />
                <Route path="pdpa/dpo" element={<DPOSettingsPage />} />
                <Route path="users" element={<ConsumersPage />} />
              </Route>

              {/* Catch-all */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </AdminAuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
